import { useCallback, useEffect, useRef, useState } from 'react'

const WARNING_MS     = 5 * 60 * 1000   // aviso a los 5 min (2 min restantes)
const COUNTDOWN_SECS = 10             // segundos que muestra el contador

const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keydown',
  'scroll', 'touchstart', 'click',
] as const

interface UseSessionTimeoutOptions {
  onTimeout: () => void
}

export function useSessionTimeout({ onTimeout }: UseSessionTimeoutOptions) {
  const [showWarning, setShowWarning]     = useState(false)
  const [countdown, setCountdown]         = useState(COUNTDOWN_SECS)

  // Ref para consultar showWarning en el handler sin agregarlo como dep del efecto
  const showWarningRef = useRef(false)
  const warningTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logoutTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null)
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Mantener ref sincronizado con el estado
  useEffect(() => {
    showWarningRef.current = showWarning
  }, [showWarning])

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current)      clearTimeout(warningTimerRef.current)
    if (logoutTimerRef.current)       clearTimeout(logoutTimerRef.current)
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current)
  }, [])

  const startTimers = useCallback(() => {
    clearAllTimers()
    setShowWarning(false)

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true)
      setCountdown(COUNTDOWN_SECS)

      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current!)
            return 0
          }
          return prev - 1
        })
      }, 1000)

      logoutTimerRef.current = setTimeout(() => {
        setShowWarning(false)
        onTimeout()
      }, COUNTDOWN_SECS * 1000)
    }, WARNING_MS)
  }, [clearAllTimers, onTimeout])

  const continueSession = useCallback(() => {
    startTimers()
  }, [startTimers])

  // Registrar listeners una sola vez — usa ref para no reiniciar timers al abrir el modal
  useEffect(() => {
    const handleActivity = () => {
      if (!showWarningRef.current) startTimers()
    }

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    )

    startTimers()

    return () => {
      clearAllTimers()
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleActivity)
      )
    }
  }, [startTimers, clearAllTimers])  // showWarning fuera — el ref lo cubre

  return { showWarning, countdown, continueSession }
}
